import { type BookingClient, type Location, MeetingMode, type SchedulingEventType } from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { type LocationInput } from '../../../store/scheduling/dto'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { type EmailTemplateContext } from './renderEmailTemplate'

export interface EmailTemplateContextInput {
  eventType: SchedulingEventType
  client?: BookingClient
  owner?: User
  note?: string
  meetingMode?: MeetingMode
  location?: Location | LocationInput | null
  bookingLink?: string
  firmName?: string
  originalStart?: string
  timezone?: string
}

const FALLBACK_TIMEZONE = 'UTC'

const locationLabel = (mode: MeetingMode, location: Location | LocationInput | null | undefined): string => {
  if (mode === MeetingMode.VIRTUAL) {
    return 'Virtual'
  }
  return location?.address || 'In Person'
}

const resolveTimezone = (timezone: string): string => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone })
    return timezone
  } catch {
    return FALLBACK_TIMEZONE
  }
}

const formatEmailDate = (moment: Date, timeZone: string): string =>
  new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone }).format(moment)

const formatEmailTime = (moment: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone }).formatToParts(moment)
  const clock = parts
    .filter((part) => part.type === 'hour' || part.type === 'minute' || (part.type === 'literal' && part.value === ':'))
    .map((part) => part.value)
    .join('')
  const period = parts.find((part) => part.type === 'dayPeriod')?.value ?? ''
  return `${clock} ${period}`
}

const formatEmailTimezone = (moment: Date, timeZone: string): string =>
  new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone }).formatToParts(moment).find((part) => part.type === 'timeZoneName')?.value ?? timeZone

const originalTimeFields = (originalStart: string | undefined, preferredTimezone: string): Record<string, string> => {
  if (!originalStart) {
    return {}
  }
  const moment = new Date(originalStart)
  if (Number.isNaN(moment.getTime())) {
    return {}
  }
  const timezone = resolveTimezone(preferredTimezone)
  return {
    original_date: formatEmailDate(moment, timezone),
    original_time: formatEmailTime(moment, timezone),
    timezone: formatEmailTimezone(moment, timezone),
  }
}

export const buildEmailTemplateContext = ({
  eventType,
  client,
  owner,
  note,
  meetingMode,
  location,
  bookingLink,
  firmName,
  originalStart,
  timezone,
}: EmailTemplateContextInput): EmailTemplateContext => ({
  client_first_name: client?.firstName || client?.email?.split('@')[0] || 'there',
  advisor_first_name: owner?.firstName ?? '',
  advisor_last_name: owner?.lastName ?? '',
  advisor_full_name: owner ? getUserDisplayName(owner) : '',
  advisor_message: note ?? '',
  event_type_name: eventType.name,
  duration: `${eventType.durationMinutes} minutes`,
  location: locationLabel(meetingMode ?? eventType.meetingMode, location),
  booking_link: bookingLink ?? '',
  firm_name: firmName ?? '',
  ...originalTimeFields(originalStart, timezone || owner?.effectiveSettings?.timezone || FALLBACK_TIMEZONE),
})
